import { useState } from "react";
import { toast } from "react-toastify";
import { request } from "../../../config/helpers/axios-instance";
import {
    generateLabel,
    copyTrackingLink,
    getDeliveryTrackingLink,
    isManualDelivery,
} from "../../../components/delivery/helper";
import { DELIVERY_PARTNER_IDS_MAP, DELIVERY_STATUS } from "../../../components/delivery/constants";

// Row actions for the logistics console.
//
// Every call here is the SAME endpoint the legacy /delivery page calls
// (src/pages/delivery/index.jsx) — this console changes how actions are PRESENTED
// (recommendation-first, spec §5), never what they do. `generateLabel` and
// `copyTrackingLink` are imported from the legacy helper rather than reimplemented.
//
// `busy` holds the id of the delivery currently mid-request, so a row can disable its own
// buttons without freezing the whole table.
export default function useDeliveryActions({ onChanged } = {}) {
    const [busy, setBusy] = useState(null);

    const run = async (id, fn) => {
        setBusy(id);
        try {
            await fn();
        } finally {
            setBusy(null);
        }
    };

    const post = async (url, successMessage, failureMessage) => {
        try {
            const response = await request({
                method: "POST",
                headers: { "Content-Type": "application/json" },
                url,
            });
            if (response.data?.success) {
                toast.success(successMessage);
                if (onChanged) onChanged();
                return true;
            }
            toast.error(response.data?.err || failureMessage);
        } catch (error) {
            toast.error(failureMessage);
        }
        return false;
    };

    const handle = (action, delivery) => {
        const id = delivery?.id;
        if (!id) return;

        switch (action) {
            case "label":
                return run(id, () => generateLabel(id));
            case "copy":
                return copyTrackingLink(getDeliveryTrackingLink(delivery));
            case "track":
                // Handled by the page: it opens the shared DeliveryTrackingModal, which does
                // its own fetch. Listed here so an unrouted "track" is an obvious no-op
                // rather than falling through to `default` unnoticed.
                return undefined;
            case "delivered":
                return run(id, () => post(`delivery/markDelivered/${id}`, "Marked delivered", "Error marking delivered"));
            case "cancel":
                return run(id, () => post(`delivery/cancel/${id}`, "Delivery cancelled", "Error cancelling delivery"));
            case "manifest":
                // Path is `generateManifest`, NOT `generate-manifest`. The hyphenated spelling was
                // carried over from src/pages/delivery/index.jsx and matched no route, so this
                // button 404'd on both pages. Corrected 2026-09-23; the route is
                // routes/api/delivery.js POST /generateManifest/:id.
                return run(id, () => post(`delivery/generateManifest/${id}`, "Manifest generated", "Error generating manifest"));
            case "downloadManifest": {
                const url = delivery?.courierDetails?.manifestUrl;
                if (url) window.open(url, "_blank", "noopener,noreferrer");
                return undefined;
            }
            case "review":
                // No endpoint of its own — ERROR rows are worked through the normal actions
                // (HANDOFF.md: errors stay inside the queue, no separate exceptions product).
                toast.info("Use Generate Label or Cancel to resolve this shipment.");
                return undefined;
            default:
                return undefined;
        }
    };

    // Bulk actions over the rows ticked in the table — the SAME two endpoints the legacy
    // /delivery page's selection buttons call (src/pages/delivery/index.jsx).
    //
    // `bulkBusy` is the action name in flight, separate from `busy`, so a bulk run disables
    // the bulk buttons without making every row read "Working…".
    //
    // Both resolve to true on success so the page knows to clear its selection.
    const [bulkBusy, setBulkBusy] = useState(null);

    const runBulk = async (action, fn) => {
        setBulkBusy(action);
        try {
            return await fn();
        } finally {
            setBulkBusy(null);
        }
    };

    // POST /delivery/generateLabels merges DTDC labels into ONE PDF and ignores every other
    // partner (controller/delivery.js generateLabels). Filtering here rather than letting the
    // API drop them silently means the operator is told which rows were skipped.
    const generateLabels = (deliveries) =>
        runBulk("labels", async () => {
            const eligible = deliveries.filter(
                (row) =>
                    parseInt(row?.deliveryPartnerId, 10) === DELIVERY_PARTNER_IDS_MAP.DTDC &&
                    row?.status !== DELIVERY_STATUS.CANCELLED &&
                    !isManualDelivery(row)
            );
            if (eligible.length === 0) {
                toast.warn("Bulk labels are DTDC-only. None of the selected shipments qualify.");
                return false;
            }
            try {
                const response = await request({
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    url: "delivery/generateLabels",
                    data: { deliveryIds: eligible.map((row) => row.id) },
                });
                const data = response.data?.data;
                const labelUrl = typeof data === "string" ? data : data?.labelUrl;
                if (!response.data?.success || !labelUrl) {
                    toast.error(response.data?.err || "Error generating labels");
                    return false;
                }
                const skipped = deliveries.length - eligible.length;
                toast.success(
                    skipped > 0
                        ? `Labels generated for ${eligible.length}; ${skipped} skipped (not DTDC)`
                        : `Labels generated for ${eligible.length}`
                );
                window.open(labelUrl, "_blank", "noopener,noreferrer");
                return true;
            } catch (error) {
                toast.error("Error generating labels");
                return false;
            }
        });

    // POST /delivery/markDeliveredBulk processes each row independently and reports a
    // per-row result, so a partial failure is a warning, not an error. Capped at 50 by the
    // API — the same as this page's size, so a full page always fits in one call.
    const markDelivered = (deliveries) =>
        runBulk("delivered", async () => {
            try {
                const response = await request({
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    url: "delivery/markDeliveredBulk",
                    data: { deliveries: deliveries.map((row) => ({ id: row.id })) },
                });
                if (!response.data?.success) {
                    toast.error(response.data?.err || "Error marking deliveries as delivered");
                    return false;
                }
                const { succeeded, failed, total } = response.data.data.summary;
                if (failed === 0) toast.success(`${succeeded} of ${total} marked as delivered`);
                else toast.warn(`${succeeded} marked as delivered, ${failed} failed`);
                if (onChanged) onChanged();
                return true;
            } catch (error) {
                toast.error("Error marking deliveries as delivered");
                return false;
            }
        });

    return { handle, busy, bulk: { generateLabels, markDelivered }, bulkBusy };
}
